import { useEffect, useState } from 'react';
import Select from 'react-select';

import { toastError, toastSuccess } from '@components/Toast';

import ApiClient from '@lib/apiClient';

import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';
import { useGetUserByTypeWithExternalUserQuery } from '@/api/commonApis';
import { SELECT_STYLES, getText, toSelectOptions, toSelectedOptions } from '@/features/dashboard/utils';
import {
	useEditWiseCardUsersQuery,
	useGetActiveDashboardUsersQuery,
	useGetActiveUsersQuery,
	useSetDashboardCardMutation,
	useUpdateDashboardCardMutation
} from '@/features/dashboard/api/DashboardApi';
import LoaderComponent from '@/components/LoaderComponent';

const AddAndEditDashboardModal = ({ selectedCard = null, onClose = () => {}, onSuccess = () => {} }) => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [detailsUrl, setDetailsUrl] = useState('');
	const [selectedPrimaryUserTypes, setSelectedPrimaryUserTypes] = useState([]);
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [roleList, setRoleList] = useState([]);
	const [tagInput, setTagInput] = useState('');
	const [tags, setTags] = useState([]);
	const [individualUserSearch, setIndividualUserSearch] = useState('');

	const [setDashboardCard, { isLoading: isSaving }] = useSetDashboardCardMutation();
	const [updateDashboardCard, { isLoading: isUpdating }] = useUpdateDashboardCardMutation();
	const isEditMode = Boolean(selectedCard?.id);

	const { data: activeUsers = [] } = useGetActiveUsersQuery(
		{ search: individualUserSearch.trim() },
		{
			refetchOnMountOrArgChange: true
		}
	);

	const { data: userTypeWithExternalUser = [] } = useGetUserByTypeWithExternalUserQuery();
	const selectedPrimaryUserTypeValues = selectedPrimaryUserTypes
		.map((item) => item?.value || item)
		.filter(Boolean);
	const selectedRoleFilterValues = selectedRoles
		.map((item) => item?.value || item?.id || item)
		.filter(Boolean);

	const { data: activeDashboardUsers = [] } = useGetActiveDashboardUsersQuery({
		userTypes: selectedPrimaryUserTypeValues,
		roleIds: selectedRoleFilterValues
	});

	const { data: editWiseCardUsers = [] } = useEditWiseCardUsersQuery(
		{ id: selectedCard?.id },
		{
			skip: !isEditMode || !selectedCard?.id,
			refetchOnMountOrArgChange: true
		}
	);

	///all options
	const primaryUserTypeOptions = toSelectOptions(userTypeWithExternalUser);
	const roleOptions = toSelectOptions(roleList);
	const individualUserOptions = toSelectOptions(activeUsers);
	const allUserOptions = toSelectOptions(activeDashboardUsers);
	const visibleUserOptions = isEditMode
		? toSelectOptions([...editWiseCardUsers, ...activeDashboardUsers])
		: allUserOptions;
	const mergedUserOptions = toSelectOptions([...visibleUserOptions, ...selectedUsers]);

	///for selected values
	const selectedRoleIds = selectedRoles.map((item) => item?.id).filter(Boolean);
	const selectedUserIds = selectedUsers.map((item) => item?.id).filter(Boolean);
	const selectedPrimaryUserTypeIds = selectedPrimaryUserTypes.map((item) => item?.value).filter(Boolean);

	const removeByValue = (setter, value) => {
		setter((prev) => prev.filter((item) => item.value !== value));
	};

	const resetAccessSelections = () => {
		setSelectedPrimaryUserTypes([]);
		setSelectedRoles([]);
		setSelectedUsers([]);
	};

	const addTag = () => {
		const value = tagInput.trim();
		if (!value) {
			return;
		}

		if (tags.includes(value)) {
			setTagInput('');
			return;
		}

		setTags((prev) => [...prev, value]);
		setTagInput('');
	};

	const removeTag = (tagValue) => {
		setTags((prev) => prev.filter((tag) => tag !== tagValue));
	};

	const handleSave = async () => {
		if (!title.trim()) {
			toastError('Title is required');
			return;
		}

		const requestTags = Array.from(
			new Set([...tags, tagInput.trim()].map((tag) => String(tag).trim()).filter(Boolean))
		);

		const requestBody = {
			title: title.trim(),
			description: description.trim(),
			detailsUrl: detailsUrl.trim(),
			tags: requestTags,
			allowedUserIds: selectedUserIds,
			allowedUserLevelIds: selectedRoleIds,
			allowedUserTypes: selectedPrimaryUserTypeIds
		};

		try {
			if (isEditMode) {
				await updateDashboardCard({ id: selectedCard.id, requestBody }).unwrap();
				toastSuccess('Dashboard card updated successfully');
			} else {
				await setDashboardCard({ requestBody }).unwrap();
				toastSuccess('Dashboard card created successfully');
			}
			setTags(requestTags);
			setTagInput('');
			onSuccess();
			onClose();
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong! Please try again.');
		}
	};

	const getRoleWithDepartment = async () => {
		await ApiClient.GET('getRoleWithDepartment')
			.then(({ data = [] }) => {
				setRoleList(
					data.map((item) => ({
						value: item?.id,
						label: item?.name
					}))
				);
			})
			.catch(({ response }) => {
				if (response?.data?.message) {
					toastError(response.data.message);
				}
			});
	};

	useEffect(() => {
		getRoleWithDepartment();
	}, []);

	useEffect(() => {
		setTitle(selectedCard?.title || '');
		setDescription(selectedCard?.description || '');
		setDetailsUrl(selectedCard?.detailsUrl || '');
		setTags(
			(selectedCard?.tags || selectedCard?.tagIds || []).map((tag) => getText(tag)).filter(Boolean)
		);
	}, [selectedCard]);

	useEffect(() => {
		if (isEditMode) {
			const roleSource = selectedCard?.allowedUserLevels || selectedCard?.allowedUserLevelIds || [];

			const userSource = selectedCard?.allowedUsers || selectedCard?.allowedUserIds || [];

			setSelectedPrimaryUserTypes(
				toSelectedOptions(
					selectedCard?.allowedUserTypes || [],
					toSelectOptions(userTypeWithExternalUser)
				)
			);
			setSelectedRoles(toSelectedOptions(roleSource, toSelectOptions(roleList)));
			setSelectedUsers(toSelectedOptions(userSource, toSelectOptions(activeUsers)));
			return;
		}

		resetAccessSelections();
	}, [selectedCard, isEditMode, userTypeWithExternalUser, roleList]);

	return (
		<LoaderComponent isLoading={isSaving || isUpdating}>
			<ModalContainer
				className="add-edit-dashboard-modal width-784-dialog"
				onCloseModal={onClose}
				isSaticBackdrop={true}
			>
				<div className="popup-wrapper">
					<div className="common-popup body-scroll">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">
									{isEditMode ? 'Edit dashboard card' : 'Add dashboard card'}
								</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<CommonImage src="/icons/close.svg" alt="close" width={20} height={20} />
							</div>
						</div>

						<div className="common-popup-body scroll-y-label pb-0">
							<div className="row">
								<div className="col-lg-6 pr-lg-3">
									<div className="form-group">
										<label>Title</label>
										<input
											type="text"
											placeholder="Enter dashboard title"
											value={title}
											onChange={(e) => setTitle(e.target.value)}
										/>
									</div>

									<div className="form-group">
										<label>Description</label>
										<textarea
											rows={4}
											placeholder="Enter dashboard description"
											value={description}
											onChange={(e) => setDescription(e.target.value)}
										/>
									</div>

									<div className="form-group">
										<label>URL</label>
										<input
											type="text"
											placeholder="Enter details URL"
											value={detailsUrl}
											onChange={(e) => setDetailsUrl(e.target.value)}
										/>
									</div>

									<div className="form-group mb-0">
										<label>Tags</label>
										<div className="d-flex align-items-center gap-2 tag-input-row">
											<input
												type="text"
												placeholder="Type a tag and press Enter"
												value={tagInput}
												onChange={(e) => setTagInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault();
														addTag();
													}
												}}
											/>
											<button
												type="button"
												className="button gray-outline size32 px-3 gray_dark_02"
												onClick={addTag}
											>
												Add
											</button>
										</div>
										<div className="dashboard-tag-list mt-2">
											<div className="chips">
												{tags.map((tag) => (
													<div
														key={tag}
														className="chip gray-chip radius-1 d-inline"
													>
														<span>{tag}</span>
														<span
															className="chip-close cursor-pointer"
															onClick={() => removeTag(tag)}
														>
															<CommonImage
																src="/icons/close.svg"
																alt="remove"
																width="12"
																height="12"
															/>
														</span>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>

								<div className="col-lg-6 pl-lg-3 mt-3 mt-lg-0">
									<div className="dashboard-access-section">
										<h4 className="semibold-16 mb-1">Who can see this card?</h4>
										<p className="regular-12 gray_dark_03 mb-3">
											Combine any of the options below. Leave everything empty to make
											it visible to all users.
										</p>

										<div className="form-group no-shadow-react-select">
											<label>Primary user type</label>
											{/* selected items */}
											<div className="d-flex flex-wrap gap-2 mb-2">
												{selectedPrimaryUserTypes.map((item) => (
													<span
														key={item.value}
														className="status gray-1 radius d-inline-flex align-items-center gap-1"
													>
														{item.label}
														<span
															className="chip-close cursor-pointer"
															onClick={() =>
																setSelectedPrimaryUserTypes(
																	selectedPrimaryUserTypes.filter(
																		(v) => v.value !== item.value
																	)
																)
															}
														>
															<CommonImage
																src="/icons/close.svg"
																alt="remove"
																width="12"
																height="12"
															/>
														</span>
													</span>
												))}
											</div>
											<Select
												isMulti
												isClearable={false}
												backspaceRemovesValue={false}
												controlShouldRenderValue={false}
												styles={SELECT_STYLES}
												options={primaryUserTypeOptions}
												value={selectedPrimaryUserTypes}
												onChange={(value) => setSelectedPrimaryUserTypes(value || [])}
												placeholder="Search user types..."
											/>
										</div>

										<div className="form-group mb-3 select-component no-shadow-react-select">
											<label>Role</label>
											<div className="d-flex flex-wrap gap-2 mb-2">
												{selectedRoles.map((item) => (
													<span
														key={item.value}
														className="status gray-1 radius d-inline-flex align-items-center gap-1"
													>
														{item.label}
														<span
															className="chip-close cursor-pointer"
															onClick={() =>
																removeByValue(setSelectedRoles, item.value)
															}
														>
															<CommonImage
																src="/icons/close.svg"
																alt="remove"
																width="12"
																height="12"
															/>
														</span>
													</span>
												))}
											</div>
											<Select
												isMulti
												isClearable={false}
												backspaceRemovesValue={false}
												controlShouldRenderValue={false}
												styles={SELECT_STYLES}
												options={roleOptions}
												value={selectedRoles}
												onChange={(value) => setSelectedRoles(value || [])}
												placeholder="Search roles..."
											/>
										</div>

										<div className="form-group mb-3 no-shadow-react-select">
											<label>Individual users</label>
											<div className="d-flex flex-wrap gap-2 mb-2">
												{selectedUsers.map((item) => (
													<span
														key={item.value}
														className="status gray-1 radius d-inline-flex align-items-center gap-1"
													>
														{item.label}
														<span
															className="chip-close cursor-pointer"
															onClick={() =>
																removeByValue(setSelectedUsers, item.value)
															}
														>
															<CommonImage
																src="/icons/close.svg"
																alt="remove"
																width="12"
																height="12"
															/>
														</span>
													</span>
												))}
											</div>
											<Select
												isMulti
												isClearable={false}
												backspaceRemovesValue={false}
												controlShouldRenderValue={false}
												styles={SELECT_STYLES}
												options={individualUserOptions}
												value={selectedUsers}
												onChange={(value) => setSelectedUsers(value || [])}
												onInputChange={(value, meta) => {
													if (meta?.action === 'input-change') {
														setIndividualUserSearch(value || '');
													}
													return value;
												}}
												inputValue={individualUserSearch}
												placeholder="Search by name or email..."
											/>
										</div>

										<div className="dashboard-visible-users border p-2">
											<p className="semibold-14 mb-2">
												Visible users ({mergedUserOptions.length})
											</p>
											<div
												className="dashboard-user-chip-list scroll-y-label"
												style={{ height: '150px' }}
											>
												<div className="d-flex flex-wrap gap-2">
													{mergedUserOptions.map((user) => (
														<span
															key={user.value}
															className="status gray-1 radius"
														>
															{user.label}
														</span>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer dashboard-modal-footer">
							<div className="footer-buttons text-right">
								<button
									type="button"
									className="button outline size32 ml-2"
									onClick={onClose}
								>
									Cancel
								</button>
								<button
									type="button"
									className="button size32 ml-2"
									onClick={handleSave}
									disabled={isSaving || isUpdating}
								>
									{isEditMode ? 'Update' : 'Save changes'}
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default AddAndEditDashboardModal;
